import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Paper, Typography, Box, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  ChatHistoryEntry,
  AdminChatHistoryService,
} from 'us-web-services';
import PageStyles from '../../styles/PageStyles';
import DateUtil from '../../util/DateUtil';
import DisplayService from '../../util/DisplayService';

function ChatHistoryConversation(props: RouteComponentProps<{ userId: string }>) {
  const { history, match } = props;
  const { classes } = PageStyles();
  const [chatHistory, setChatHistory] = useState<ChatHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = Number(match.params.userId);

  useEffect(() => {
    if (isNaN(userId)) {
      setError('Invalid user ID');

      return;
    }

    const fetchChatHistory = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await AdminChatHistoryService.getByUserId(userId);

        setChatHistory(response.data?.data ?? []);
      } catch (err) {
        const displayed = DisplayService.getErrorResponse(err, 'Error loading chat history');

        setError(displayed.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatHistory();
  }, [userId]);

  const handleBack = () => {
    history.push('/chat-history');
  };

  return (
    <Paper className={classes.paper}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Chat History
        </Button>
        <Typography variant='h6'>
          Chat History for User {userId}
        </Typography>
      </Box>

      {isLoading && <Typography>Loading chat history...</Typography>}

      {error && <Typography color='error'>{error}</Typography>}

      {!isLoading && !error && chatHistory.length === 0 && (
        <Typography>No chat history found for this user.</Typography>
      )}

      {!isLoading && !error && chatHistory.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {chatHistory.map((entry) => (
            <Box
              key={entry.id ?? `entry-${entry.userId}-${entry.created}`}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <Typography variant='caption' color='textSecondary' sx={{ display: 'block', mb: 1 }}>
                {entry.created ? DateUtil.getReadableDateTime(entry.created) : ''}
              </Typography>

              <Box sx={{ mb: 1 }}>
                <Typography variant='subtitle2' color='primary'>
                  User:
                </Typography>
                <Typography variant='body1'>{entry.requestMessage}</Typography>
              </Box>

              <Box>
                <Typography variant='subtitle2' color='secondary'>
                  Assistant:
                </Typography>
                <Typography variant='body1'>{entry.responseMessage}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default withRouter(ChatHistoryConversation);
